import { styled } from "@mui/material/styles";
import { Button, Box, ButtonProps, Modal, Paper, Grow } from "@mui/material";
import { PLAYER } from "../types/players";
import { createRef, useEffect, useState } from "react";
import colors from "../theme/colors";
import fonts from "../theme/fonts";
import { DART_HITS, DART_NUMBER } from "../types/dart";
import { getGestureLocalStorage } from "../util/localStorage";
import { teamOnePlayers } from "../types/teams";

interface ScoreButtonProps {
  number: number;
  multiplier: number;
  player: PLAYER;
  disabled?: boolean;
  onClickHandler: Function;
  setButtonHeight?: Function;
  mobileButtons: boolean;
}

interface ColorButtonProps extends ButtonProps {
  player: PLAYER;
  multiple: boolean;
}

export default function ScoreButton({
  number,
  multiplier,
  player,
  disabled = false,
  onClickHandler,
  setButtonHeight,
  mobileButtons = false,
}: ScoreButtonProps) {
  const ref = createRef();
  const [showModal, setShowModal] = useState(false);
  const [useMobileGesture, setUseMobileGesture] = useState(
    getGestureLocalStorage()
  );
  const gesture = getGestureLocalStorage();
  let startX = 0;

  useEffect(() => {
    const localGesture = getGestureLocalStorage();
    if (localGesture === useMobileGesture) return;
    setUseMobileGesture(gesture);
  }, [gesture, useMobileGesture]);

  useEffect(() => {
    if (!ref || !ref.current || !setButtonHeight) return;
    //@ts-ignore
    setButtonHeight(ref?.current?.clientHeight);
    // eslint-disable-next-line
  }, [ref, ref.current, setButtonHeight]);

  const scoreHandler = (multiple: number) => {
    if (mobileButtons && !useMobileGesture && !showModal) {
      setShowModal(true);
      return;
    }
    setShowModal(false);
    onClickHandler(number, multiple);
  };

  const modalButtons = () => {
    const buttons = [];
    const multiples = number === DART_NUMBER.BULL ? 2 : 3;

    for (let i = 1; i <= multiples; i++) {
      buttons.push(
        <ColorButton
          key={`${number}_${i}_modal`}
          disabled={disabled}
          variant="contained"
          onClick={() => scoreHandler(i)}
          player={player}
          multiple={i > 1}
        >
          {i === 1 ? (number !== 25 ? number : "Bull") : `x${i}`}
        </ColorButton>
      );
    }

    return buttons;
  };

  const onTouchStart = (event: React.TouchEvent) => {
    startX = event.changedTouches[0].screenX;
  };

  const onTouchEnd = (event: React.TouchEvent) => {
    if (!mobileButtons) {
      return;
    }
    if (mobileButtons && !useMobileGesture && !showModal) {
      setShowModal(true);
      return;
    }
    const endX = event.changedTouches[0].screenX;
    const xDiff = startX - endX;
    let hit = DART_HITS.SINGLE;
    if (xDiff < -10) {
      hit = DART_HITS.TRIPLE;
    } else if (xDiff > 10) {
      hit = DART_HITS.DOUBLE;
    }
    scoreHandler(hit);
  };

  return (
    <Box ref={ref} height="100%" width="100%">
      <Grow in={true} timeout={!mobileButtons ? multiplier * 1000 : 0}>
        <ColorButton
          disabled={disabled}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          onMouseUpCapture={() => {
            if (mobileButtons) return;
            scoreHandler(multiplier);
          }}
          variant="contained"
          player={player}
          multiple={multiplier > 1}
        >
          {multiplier === 1
            ? number !== 25
              ? number
              : "Bull"
            : `x${multiplier}`}
        </ColorButton>
      </Grow>
      <Modal
        open={showModal}
        onClose={() => {
          setShowModal(false);
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Paper elevation={6}>
          <Box sx={style}>{modalButtons()}</Box>
        </Paper>
      </Modal>
    </Box>
  );
}

const ColorButton = styled(Button)<ColorButtonProps>(
  ({ player, multiple }) => ({
    textTransform: "none",
    fontSize: fonts.size.mediumLarge,
    fontFamily: fonts.family.nice,
    backgroundColor: teamOnePlayers.includes(player)
      ? multiple
        ? colors.darkGreen
        : colors.lightBeige
      : multiple
      ? colors.darkRed
      : colors.darkGray,
    color: teamOnePlayers.includes(player)
      ? multiple
        ? colors.lightBeige
        : colors.darkGray
      : colors.lightBeige,
    height: "100%",
    width: "100%",
    "&:hover": {
      backgroundColor: teamOnePlayers.includes(player)
        ? multiple
          ? colors.lightGreen
          : colors.darkBeige
        : multiple
        ? colors.lightRed
        : colors.lightGray,
    },
    "&:disabled": {
      backgroundColor: teamOnePlayers.includes(player)
        ? multiple
          ? "#689276"
          : "#c7ab8d"
        : multiple
        ? "rgba(120, 50, 50, .8)"
        : "rgba(138, 138, 138, .8)",
    },
    "@media (max-width: 600px)": {
      fontSize: fonts.size.medium,
    },
  })
);

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80vw",
  maxWidth: 400,
  bgcolor: colors.lightGreen,
  boxShadow: 24,
  p: 4,
  display: "flex",
  gap: "8px",
};
